// GlobalNavDrawer.js (revised — single-column, dark-on-dark)
// - Icon + tiny label tiles in a single vertical column
// - Dark drawer + light text + subtle light outline on tiles
// - Always-on-top via root portal + giant z-index
// - Pointer-events safety over iframes (YouTube, VoiceBot)

import React from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";

// MUI
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

// Icons
import MenuIcon from "@mui/icons-material/Menu";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import DescriptionIcon from "@mui/icons-material/Description";
import ForumIcon from "@mui/icons-material/Forum";
import WorkIcon from "@mui/icons-material/Work";
import GavelIcon from "@mui/icons-material/Gavel";
import ShieldIcon from "@mui/icons-material/Shield";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LogoutIcon from "@mui/icons-material/Logout";

// Large, but below Number.MAX_SAFE_INTEGER to keep MUI happy
const BASE_Z = 2147483000; // near max 32‑bit int
const OFFSET = 150; // headroom over most overlays

function ensurePortalEl() {
  if (typeof document === "undefined") return undefined;
  // Prefer a dedicated floating root if present (index.html)
  let el = document.getElementById("vb-floating-root");
  if (!el) {
    el = document.createElement("div");
    el.id = "vb-floating-root";
    document.body.appendChild(el);
  }
  return el;
}

function iconFor(label = "", to = "") {
  const l = label.toLowerCase();
  const p = to.toLowerCase();
  if (l.startsWith("tokens") || p.includes("/purchase")) return MonetizationOnIcon;
  if (l.includes("ispeakwell") || p.includes("/voicebot")) return RecordVoiceOverIcon;
  if (l.includes("resume")) return DescriptionIcon;
  if (l.includes("convos")) return ForumIcon;
  if (l.includes("interview")) return WorkIcon;
  if (l.includes("terms")) return GavelIcon;
  if (l.includes("privacy")) return ShieldIcon;
  if (l.includes("admin")) return AdminPanelSettingsIcon;
  if (l.includes("logout") || p.includes("/login")) return LogoutIcon;
  return DescriptionIcon; // fallback
}

/**
 * Props
 * - links: [{ to, label, onClick }]
 * - hideCurrent: boolean — hide link that points to current route
 * - anchor: 'left' | 'right' — default 'left'
 * - buttonOffset: { top: number, left: number } — menu button fixed offset
 */
function GlobalNavDrawer({
  links = [],
  hideCurrent = false,
  anchor = "left",
  buttonOffset = { top: 70, left: 30 },
}) {
  const [open, setOpen] = React.useState(false);
  const here = useLocation().pathname;
  const navigate = useNavigate();
  const portalEl = ensurePortalEl();

  // While open, prevent page scroll and neutralize underlying iframes
  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const html = document.documentElement;
    if (open) html.classList.add("vb-drawer-open");
    else html.classList.remove("vb-drawer-open");
    return () => html.classList.remove("vb-drawer-open");
  }, [open]);

  const filtered = (links || []).filter((lnk) => {
    if (!lnk) return false;
    const tgt = (lnk.to || "").replace(/\/?$/, "");
    if (!tgt && !lnk.onClick) return true; // allow actions
    const current = here.replace(/\/?$/, "");
    if (hideCurrent && tgt === current) return false;
    return true;
  });

  const handleGo = (lnk) => async () => {
    setOpen(false);
    try {
      if (typeof lnk.onClick === "function") await lnk.onClick();
    } catch (e) {
      /* noop */
    }
    if (lnk.to) navigate(lnk.to);
  };

  if (!portalEl) return null;

  return createPortal(
    <>
      {/* Floating menu button (dark on dark with light outline) */}
      <IconButton
        aria-label="Open navigation"
        onClick={() => setOpen(true)}
        sx={{
          position: "fixed",
          top: buttonOffset.top,
          left: anchor === "left" ? buttonOffset.left : "auto",
          right: anchor === "right" ? buttonOffset.left : "auto",
          zIndex: BASE_Z + OFFSET + 2,
          boxShadow: 0,
          bgcolor: "grey.900",
          color: "grey.100",
          border: "1px solid rgba(255,255,255,0.35)",
          "&:hover": { bgcolor: "grey.800", borderColor: "rgba(255,255,255,0.55)" },
        }}
      >
        <MenuIcon />
      </IconButton>

      {/* Drawer */}
      <Drawer
        anchor={anchor}
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true, container: portalEl }}
        PaperProps={{
          sx: {
            zIndex: BASE_Z + OFFSET,
            position: "fixed",
            pt: "calc(env(safe-area-inset-top) + 180px)",
            pb: "env(safe-area-inset-bottom)",
            top: 0,
            left: 0,
            height: "100%",
            width: 120,
            bgcolor: "grey.900",
            color: "grey.100",
            borderRight: anchor === "left" ? "1px solid rgba(255,255,255,0.12)" : undefined,
            borderLeft: anchor === "right" ? "1px solid rgba(255,255,255,0.12)" : undefined,
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr", // single column
              gap: 0.75,
            }}
          >
            {filtered.map(({ to, label, onClick }) => {
              const Icon = iconFor(label, to);
              return (
                <IconButton
                  key={to || label}
                  onClick={handleGo({ to, label, onClick })}
                  sx={{
                    width: "90%",
                    height: 62,
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 2,
                    boxShadow: 0,
                    bgcolor: "rgba(0,0,0,0.35)", // dark on dark tile
                    color: "grey.100", // light writing
                    border: "1px solid rgba(255,255,255,0.3)", // light outline
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.08)",
                      borderColor: "rgba(255,255,255,0.5)",
                    },
                  }}
                >
                  <Icon fontSize="medium" />
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, lineHeight: 1.1, textAlign: "center", opacity: 0.95 }}
                  >
                    {label}
                  </Typography>
                </IconButton>
              );
            })}
          </Box>
        </Box>
      </Drawer>

      {/* Scoped styles for stacking + pointer-events safety */}
      <style>{`
        html.vb-drawer-open, html.vb-drawer-open body { overflow: hidden !important; }
        /* The floating root spans the viewport and sits above app content */
        #vb-floating-root {
          position: fixed; inset: 0; z-index: ${BASE_Z + OFFSET + 1};
          pointer-events: none;
        }
        #vb-floating-root * { pointer-events: auto; }
        /* While open, block underlying iframes from stealing clicks */
        html.vb-drawer-open .VB-stage iframe,
        html.vb-drawer-open iframe { pointer-events: none !important; }
      `}</style>
    </>,
    portalEl
  );
}

export default React.memo(GlobalNavDrawer);
